import { useEffect, useRef, useState } from "react";
import { eqlAggregate } from "../../../../apis/apiEQL";
import { useReportContext } from "../../../Report/ReportContext/useReportContext";

// Canonical status order (matches status_locales, "in ascending order") and an
// icon per status. Icons are ones known to exist in the icon map.
// `draft`, `closed` and `closing_process` are intentionally excluded from the
// KPI cards (see EXCLUDED_STATUSES).
const STATUS_ORDER = [
  "new",
  "pending",
  "process",
  "finish",
];

// Statuses that should never appear as indicator cards.
const EXCLUDED_STATUSES = new Set(["draft", "closed", "closing_process"]);

const STATUS_ICON = {
  new: "bold_document_text_1",
  pending: "bold_status",
  process: "bold_status",
  finish: "bold_tick_circle",
};

// Turns an EQL status aggregate into ordered indicator cards.
const buildIndicators = (resp, status_locales) => {
  const counts = new Map((resp?.data || []).map((r) => [r.value, r.count]));

  // Always show every canonical status (0 when the year has none), then append
  // any unexpected statuses that actually have reports.
  const extra = [...counts.keys()].filter(
    (s) => s && !STATUS_ORDER.includes(s) && !EXCLUDED_STATUSES.has(s)
  );

  return [...STATUS_ORDER, ...extra].map((s) => ({
    id: s,
    label: status_locales?.[s]?.label || String(s),
    value: counts.get(s) || 0,
    accentColor: status_locales?.[s]?.color,
    iconName: STATUS_ICON[s] || "bold_document_text_1",
  }));
};

/**
 * useStaticIndicators — report counts grouped by status for a given year.
 *
 * Counts come from a single EQL aggregate over the reports created that year;
 * labels/colors come from the tenant's status_locales. Results are cached per
 * year, so switching back to an already-loaded year never refetches, and a year
 * is only requested once it's actually selected (lazy).
 *
 * @param {number} year - calendar year to load
 * @returns {{ indicators: Array<{id,label,value,accentColor,iconName}>, loading: boolean }}
 */
export const useStaticIndicators = (year) => {
  const { status_locales } = useReportContext();
  const cacheRef = useRef(new Map()); // year -> indicators[]
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // Serve cached year without hitting the endpoint again.
    if (cacheRef.current.has(year)) {
      setIndicators(cacheRef.current.get(year));
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const filters = {
          AND: [
            { field: "created_at", operator: "gte", value: `${year}-01-01` },
            { field: "created_at", operator: "lte", value: `${year}-12-31` },
          ],
        };
        const resp = await eqlAggregate({
          groupBy: "status.current",
          // Drop excluded statuses server-side so they never come back.
          exclude: [...EXCLUDED_STATUSES],
          filters,
        });
        if (!active) return;

        const built = buildIndicators(resp, status_locales);
        cacheRef.current.set(year, built);
        setIndicators(built);
      } catch (e) {
        console.error("useStaticIndicators: EQL aggregate failed", e);
        if (active) setIndicators([]);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // status_locales is rebuilt each render but stable for a given language;
    // only the selected year should drive a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  return { indicators, loading };
};

export default useStaticIndicators;
